// ─── DESIGN TOKENS ───────────────────────────────────────────────
// Same surface palette as Camilla PIM — only the accent colour changes.
// Camilla used a champagne gold; RMW gets a saddle-tan / oxblood pair
// pulled from the brand's bootmaking heritage.
export const C = {
  bg: '#0a0a0c', card: '#13131a', hover: '#18181f', input: '#16161e',
  b: '#252530', bl: '#1e1e28',
  a:    '#b4774a',   // RMW saddle tan (primary accent)
  ad:   '#b4774a22',
  rust: '#8b3a2c',   // oxblood / chestnut
  rustd:'#8b3a2c22',
  e: '#ef4444', ed: '#ef444422',
  blue: '#60a5fa', blued: '#60a5fa22',
  purp: '#a78bfa', purpd: '#a78bfa22',
  teal: '#2dd4bf', teald: '#2dd4bf22',
  good: '#22c55e', goodd:'#22c55e22',
  t: '#e4e5e7', tm: '#6b7280', td: '#4b5058', wh: '#ffffff',
};
export const F = "'Onest', system-ui, sans-serif";
export const M = "'IBM Plex Mono', monospace";

// ─── R.M.WILLIAMS STOREFRONTS ──────────────────────────────────
// Phase 1: AU only. The schema is multi-store ready — add rows here and
// the rest of the app picks them up.
export const STORES = [
  {
    id: 'au',
    label: 'Australia',
    flag: '🇦🇺',
    domain: 'rmwilliams.com.au',
    url: 'https://www.rmwilliams.com.au',
    currency: 'AUD',
    currencySymbol: 'A$',
    locale: 'en-AU',
    color: C.a,
    description: 'Australian flagship store',
    // SFCC site identifier — verify by watching a Product-Variation request
    // in DevTools and copying from /on/demandware.store/Sites-{this}-Site/...
    sfcc_site_id: 'RMWilliams-AU',
    sfcc_locale: 'en_AU',
  },
];

// ─── R.M.WILLIAMS CATEGORIES ──────────────────────────────────
// Modeled on what RMW actually sells: bootmaker first, then leather goods,
// apparel, and accessories. Icons are picked to read at small sizes.
export const DEPTS = [
  { id: 'all',          label: 'All Products',  icon: '✨', color: C.a },
  { id: 'boots',        label: 'Boots',          icon: '🥾', color: C.a },
  { id: 'shoes',        label: 'Shoes',          icon: '👞', color: '#a37052' },
  { id: 'shirts',       label: 'Shirts',         icon: '👔', color: '#60a5fa' },
  { id: 'knitwear',     label: 'Knitwear',       icon: '🧶', color: '#a78bfa' },
  { id: 'jackets',      label: 'Jackets & Outerwear', icon: '🧥', color: '#78716c' },
  { id: 'pants',        label: 'Trousers & Jeans', icon: '👖', color: '#475569' },
  { id: 'belts',        label: 'Belts',          icon: '🪢', color: C.rust },
  { id: 'wallets',      label: 'Wallets & Cardholders', icon: '💳', color: '#92400e' },
  { id: 'bags',         label: 'Bags & Luggage', icon: '👜', color: '#f59e0b' },
  { id: 'accessories',  label: 'Accessories',    icon: '🎩', color: '#94a3b8' },
  { id: 'leather_care', label: 'Leather Care',   icon: '🧴', color: '#22c55e' },
  { id: 'gift',         label: 'Gift',           icon: '🎁', color: '#ef4444' },
  { id: 'other',        label: 'Other',          icon: '📋', color: '#94a3b8' },
];

// ─── BRAND CONTEXT ───────────────────────────────────────────────
// Drives every AI prompt. Edit this file to change the brand voice.
export const RMW = {
  name: 'R.M.Williams',
  founded: 1932,
  founder: 'Reginald Murray Williams',
  hq: 'Adelaide, Australia',
  positioning: 'Australian heritage bootmaker and outfitter',
  usps: [
    { k: 'Whole-cut Construction',  d: 'The Craftsman — single piece of leather, no side seams. The signature.', i: '🥾' },
    { k: 'Goodyear Welted',         d: 'Welted construction means the boot can be re-soled and re-soled again.',  i: '🔧' },
    { k: 'Made in Adelaide',        d: 'Hand-crafted in our Salisbury workshop since 1932.',                       i: '🇦🇺' },
    { k: 'Bush Heritage',           d: 'Born in the Australian outback. Built for stockmen, worn the world over.', i: '🐎' },
    { k: 'Yearling Leather',        d: 'Premium yearling cattle hide, hand-selected for grain and finish.',         i: '🪵' },
    { k: 'Lifetime Resoleable',     d: 'Send your boots back to Adelaide for refurbishment, year after year.',     i: '♻️' },
    { k: 'B-Width to H-Width',      d: 'Genuine width fittings, not cosmetic — your foot, properly shod.',         i: '📏' },
    { k: 'Heritage 1932',           d: 'Ninety-plus years, family-founded, still made the same way.',              i: '✦' },
  ],
  voice: 'Plainspoken, confident, hard-wearing. Australian without affectation. Leans on craft and provenance — never glossy. Reads like a stockman explaining a tool, not a marketer selling a lifestyle.',
  keyMessages: [
    'Made in Australia since 1932',
    'One piece of leather, no shortcuts',
    'Built to be re-soled, not replaced',
    'The Craftsman — the boot we became famous for',
  ],
};

// Alias for code that imports CAMILLA-style — keeps the porting surface clean.
export const CAMILLA = RMW;

// ─── CATEGORY CLASSIFICATION ─────────────────────────────────────
// Same shape as Camilla's classifyProduct, retuned for menswear / leather goods.
const TYPE_MAP = {
  boots:        ['boot', 'boots', 'craftsman', 'comfort craftsman', 'dynamic flex', 'gardener', 'kangaroo'],
  shoes:        ['shoe', 'shoes', 'loafer', 'derby', 'oxford', 'sneaker', 'plimsoll'],
  shirts:       ['shirt', 'shirts', 'polo', 'overshirt', 'workshirt', 'flannel'],
  knitwear:     ['knit', 'jumper', 'sweater', 'cardigan', 'pullover', 'crewneck'],
  jackets:      ['jacket', 'coat', 'oilskin', 'longhorn', 'gilet', 'vest', 'parka'],
  pants:        ['jean', 'jeans', 'trouser', 'trousers', 'pant', 'pants', 'chino', 'moleskin', 'cord'],
  belts:        ['belt', 'belts'],
  wallets:      ['wallet', 'cardholder', 'card holder', 'card case', 'money clip', 'passport'],
  bags:         ['bag', 'tote', 'duffle', 'duffel', 'satchel', 'briefcase', 'overnight', 'weekender', 'backpack'],
  accessories:  ['hat', 'akubra', 'cap', 'scarf', 'tie', 'cufflink', 'sock', 'socks', 'umbrella', 'keyring'],
  leather_care: ['leather care', 'polish', 'cream', 'conditioner', 'brush', 'shoe horn', 'shoehorn'],
  gift:         ['gift card', 'gift voucher'],
};

export function classifyProduct(product) {
  const title = (product.title || product.name || '').toLowerCase();
  const ptype = (product.product_type || product.productType || '').toLowerCase().trim();
  const tags  = (typeof product.tags === 'string'
    ? product.tags.split(',') : (product.tags || [])).map(t => String(t).toLowerCase().trim());
  const breadcrumbs = (product.breadcrumbs || []).map(c => String(c).toLowerCase());

  // 1. Product type (strongest signal)
  if (ptype) {
    for (const [dept, types] of Object.entries(TYPE_MAP)) {
      for (const t of types) {
        if (ptype === t || ptype.includes(t)) return dept;
      }
    }
  }

  // 2. Breadcrumbs (SFCC's primary nav signal — replaces Camilla's collections)
  if (breadcrumbs.length) {
    for (const [dept, types] of Object.entries(TYPE_MAP)) {
      for (const t of types) {
        if (breadcrumbs.some(c => c === t || c.includes(t))) return dept;
      }
    }
  }

  // 3. Tags
  if (tags.length) {
    const scores = {};
    for (const [dept, types] of Object.entries(TYPE_MAP)) {
      for (const t of types) {
        if (tags.some(tag => tag === t || tag.includes(t))) {
          scores[dept] = (scores[dept] || 0) + 1;
        }
      }
    }
    const best = Object.entries(scores).sort((a, b) => b[1] - a[1])[0];
    if (best && best[1] >= 1) return best[0];
  }

  // 4. Title
  for (const [dept, types] of Object.entries(TYPE_MAP)) {
    for (const t of types) {
      if (title.includes(t)) return dept;
    }
  }

  return 'other';
}

// ─── GAP ANALYSIS ────────────────────────────────────────────────
// Matches Camilla's structure but with rules tuned for leather goods:
// fabric → leather, sizing → fit + width, fashion-specific → boot-specific.
export function computeGaps(product) {
  const gaps = [];
  const desc = product.description || product.body_html || product.bodyHtml || '';
  const plainDesc = desc.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const tags = typeof product.tags === 'string'
    ? product.tags.split(',').map(t => t.trim()).filter(Boolean)
    : (product.tags || []);
  const images = product.images || [];
  const variants = product.variants || [];
  const dept = product._ai_dept || classifyProduct(product);

  if (plainDesc.length < 80) {
    gaps.push({ id: 'desc_missing', label: 'Description missing or critically thin',
      detail: `Only ${plainDesc.length} chars (target: 200+)`, severity: 'high',
      agent: 'seo_rewrite', tab: 'ai', icon: '✏️' });
  } else if (plainDesc.length < 200) {
    gaps.push({ id: 'desc_thin', label: 'Description too short',
      detail: `${plainDesc.length} chars (target: 200+)`, severity: 'medium',
      agent: 'seo_rewrite', tab: 'ai', icon: '✏️' });
  }

  if (images.length === 0) {
    gaps.push({ id: 'no_images', label: 'No product images',
      detail: 'Need hero + side + sole + detail shots', severity: 'high',
      agent: 'image_source', tab: 'media', icon: '📸' });
  } else if (images.length <= 1) {
    gaps.push({ id: 'few_images', label: 'Only 1 image',
      detail: 'Add side, sole, and detail shots (target: 5-8 for boots)', severity: 'medium',
      agent: 'image_source', tab: 'media', icon: '📸' });
  }

  if (tags.length < 3) {
    gaps.push({ id: 'tags_critical', label: 'Critically few tags',
      detail: `Only ${tags.length} tags (target: 8-12)`, severity: 'high',
      agent: 'enrich_tags', tab: 'tags', icon: '🏷' });
  } else if (tags.length < 8) {
    gaps.push({ id: 'tags_low', label: 'Needs more tags',
      detail: `${tags.length} tags (target: 8-12)`, severity: 'medium',
      agent: 'enrich_tags', tab: 'tags', icon: '🏷' });
  }

  if (!product.seo_title && !product.metafields_global_title_tag &&
      (!product.title || (product.title || '').length < 30)) {
    gaps.push({ id: 'seo_title', label: 'SEO title weak or missing',
      detail: 'No custom title tag', severity: 'medium',
      agent: 'seo_title', tab: 'ai', icon: '🔍' });
  }

  const hasUsableMeta = plainDesc.length >= 100;
  if (!hasUsableMeta) {
    gaps.push({ id: 'meta_desc', label: 'Meta description missing',
      detail: 'Description too short to generate a quality meta tag (target: 100+ chars)',
      severity: 'medium', agent: 'seo_meta', tab: 'ai', icon: '🔍' });
  }

  // Leather-goods specific gaps
  const leatherDepts = new Set(['boots', 'shoes', 'belts', 'wallets', 'bags']);
  if (leatherDepts.has(dept)) {
    if (!plainDesc.match(/leather|yearling|kangaroo|suede|hide|grain/i)) {
      gaps.push({ id: 'no_leather', label: 'No leather/material details',
        detail: 'Add hide type, grain, finish, and care info', severity: 'medium',
        agent: 'enrich_meta', tab: 'tags', icon: '🪵' });
    }
    if (dept === 'boots' && !plainDesc.match(/welt|sole|construction|goodyear|hand-?cut/i)) {
      gaps.push({ id: 'no_construction', label: 'No construction details',
        detail: 'Boots should describe welt, sole, and construction', severity: 'low',
        agent: 'enrich_meta', tab: 'tags', icon: '🔧' });
    }
  } else {
    if (!plainDesc.match(/cotton|wool|merino|linen|fabric|composition|material/i)) {
      gaps.push({ id: 'no_fabric', label: 'No fabric/material details',
        detail: 'Add composition and care info', severity: 'medium',
        agent: 'enrich_meta', tab: 'tags', icon: '🧵' });
    }
  }

  // Boots-specific: need size+width matrix populated
  if (dept === 'boots' && variants.length > 0) {
    const inStock = variants.filter(v => v.available || v.availability === 'InStock').length;
    if (inStock === 0) {
      gaps.push({ id: 'all_oos', label: 'All sizes out of stock',
        detail: `${variants.length} variants, 0 in stock`, severity: 'high',
        agent: 'redirect', tab: 'redirects', icon: '🚫' });
    } else if (inStock / variants.length < 0.3) {
      gaps.push({ id: 'low_stock', label: 'Low stock coverage',
        detail: `Only ${inStock} of ${variants.length} variants in stock`, severity: 'medium',
        agent: 'redirect', tab: 'redirects', icon: '⚠️' });
    }
  }

  if (images.length > 0 && images.some(img => !img.alt || (img.alt || '').length < 10)) {
    gaps.push({ id: 'alt_text', label: 'Image alt text missing',
      detail: 'Images need descriptive alt text for accessibility & SEO',
      severity: 'low', agent: 'seo_meta', tab: 'media', icon: '♿' });
  }

  return gaps;
}

export function computeGapCount(product) {
  return computeGaps(product).length;
}

// Field-scoped batch action targets (used in later phases by the Batch UI).
export const GAP_TYPES = [
  { id: 'all',           label: 'All Gaps',           icon: '✨', fields: null },
  { id: 'desc_missing',  label: 'Description',        icon: '✏️', fields: ['body_html', 'headline', 'subheadline', 'why_you_love_it'] },
  { id: 'desc_thin',     label: 'Description (thin)',  icon: '✏️', fields: ['body_html', 'headline', 'subheadline', 'why_you_love_it'] },
  { id: 'meta_desc',     label: 'Meta Description',    icon: '🔍', fields: ['seo_description'] },
  { id: 'seo_title',     label: 'SEO Title',           icon: '🔍', fields: ['seo_title'] },
  { id: 'tags_critical', label: 'Tags (critical)',     icon: '🏷', fields: ['occasionTags', 'colourFamily', 'keyHighlights'] },
  { id: 'tags_low',      label: 'Tags (low)',          icon: '🏷', fields: ['occasionTags', 'colourFamily', 'keyHighlights'] },
  { id: 'alt_text',      label: 'Image Alt Text',      icon: '♿', fields: ['alt_text'] },
  { id: 'no_leather',    label: 'Leather details',     icon: '🪵', fields: ['body_html', 'why_you_love_it'] },
  { id: 'no_construction', label: 'Construction',      icon: '🔧', fields: ['body_html', 'why_you_love_it'] },
  { id: 'no_fabric',     label: 'Fabric/Material',     icon: '🧵', fields: ['body_html', 'why_you_love_it'] },
];
